import { selectCustomerInfo } from "src/stores/calllogs";
import { CallerInfo } from "./CallerInfo";
import CallerHeader from "./calllogsHeader";
import { ExistingQuery } from "./ExistingQuery";
import { QueueInfo } from "./QueueInfo";
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useState } from "react";
import axios from "axios";
import {
  trackUserAction,
  getUserActionHistory,
  UserActionType,
} from "./utils/actionTracker";
import { apiBaseUrl } from "src/services/http";

export const IncomingCall = ({ callerInfo }) => {
  const customerInfo = useSelector(selectCustomerInfo);
  const dispatch = useDispatch();
  const [isSendingActions, setIsSendingActions] = useState(false);
  const result = customerInfo?.data?.result;

  const hasQueueInfo =
    callerInfo?.callActionInput && callerInfo?.callActionQueue;

  // Get current user from localStorage
  const getCurrentUser = useCallback(() => {
    try {
      const root = JSON.parse(localStorage.getItem("persist:root") || "{}");
      const authUser = root?.authUser ? JSON.parse(root.authUser) : null;
      return authUser?.name || authUser?.username || "Unknown";
    } catch (e) {
      return "Unknown";
    }
  }, []);

  // Handle action tracking
  const handleActionTrack = useCallback(
    (actionType: string, queryId: string) => {
      const currentUser = getCurrentUser();
      const sessionId = callerInfo?.sessionId || callerInfo?.session_id;

      // Track in localStorage
      trackUserAction(actionType as any, queryId, currentUser, sessionId);

      // Dispatch to Redux
      dispatch({
        type: "CALL_ACTION_TRACKED",
        payload: {
          action: actionType,
          query_id: queryId,
          user: currentUser,
          timestamp: Date.now(),
          sessionId,
        },
      });

      console.log("Action tracked:", { actionType, queryId, currentUser });
    },
    [dispatch, getCurrentUser, callerInfo],
  );

  // Send tracked actions to API
  const sendActionsToAPI = useCallback(async () => {
    try {
      setIsSendingActions(true);
      const actions = getUserActionHistory();
      const sessionId = callerInfo?.sessionId || callerInfo?.session_id;

      if (!sessionId) {
        console.warn("No session ID available for sending actions");
        return;
      }

      // Prepare form data
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("log_text", JSON.stringify(actions));

      // Send to API
      const response = await axios.post(
        `${apiBaseUrl}/save/call-activity/log`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      console.log("Actions sent to API successfully:", response.data);

      // Dispatch success to Redux
      dispatch({
        type: "CALL_ACTIONS_SENT",
        payload: {
          sessionId,
          response: response.data,
          timestamp: Date.now(),
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error sending actions to API:", error);
      // Dispatch error to Redux
      dispatch({
        type: "CALL_ACTIONS_SEND_ERROR",
        payload: {
          error: error instanceof Error ? error.message : "Unknown error",
          timestamp: Date.now(),
        },
      });
      throw error;
    } finally {
      setIsSendingActions(false);
    }
  }, [callerInfo, dispatch]);

  // Send actions when call ends (optional hook for call completion)
  const handleCallEnd = useCallback(async () => {
    await sendActionsToAPI();
  }, [sendActionsToAPI]);

  return (
    <>
      <CallerHeader callerInfo={callerInfo} />
      <CallerInfo callerInfo={callerInfo} />

      {hasQueueInfo && <QueueInfo callerInfo={callerInfo} />}
      {result && (
        <ExistingQuery onActionTrack={handleActionTrack} code="Answered" />
      )}

      {/* Optional: Button to manually send actions */}
      {/* <div className="mt-4 flex gap-2">
        <button
          onClick={sendActionsToAPI}
          disabled={isSendingActions}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isSendingActions ? "Sending Actions..." : "Send Actions to Server"}
        </button>
      </div> */}
    </>
  );
};
